import { BuddyMood } from '@/types/game';

// ═══════════════════════════════════════════════════════
// LAYER 1: Eye & Mouth shapes (SVG paths)
// Eyes: 24×16 viewBox, Mouth: 24×12 viewBox
// REDESIGNED for bold readability at small sizes
// ═══════════════════════════════════════════════════════

export interface EyeShape {
  left: string;
  right: string;
  blinkLeft: string;
  blinkRight: string;
}

export interface MouthShape {
  d: string;
  fill?: boolean;
}

// ── EYES ──
// All shapes are bolder, more expressive, better separated moods
const EYES = {
  // Calm — heavy lids, zen, half-closed but warm
  halfClosed: {
    left:  'M4,10.5 Q4,8.5 8,8.5 Q12,8.5 12,10.5 Q12,13 8,13 Q4,13 4,10.5Z',
    right: 'M12,10.5 Q12,8.5 16,8.5 Q20,8.5 20,10.5 Q20,13 16,13 Q12,13 12,10.5Z',
    blinkLeft:  'M4,11 Q8,10 12,11',
    blinkRight: 'M12,11 Q16,10 20,11',
  },
  // Vibing — soft, friendly, slightly open
  soft: {
    left:  'M4,10 Q4,6 8,6 Q12,6 12,10 Q12,13.5 8,13.5 Q4,13.5 4,10Z',
    right: 'M12,10 Q12,6 16,6 Q20,6 20,10 Q20,13.5 16,13.5 Q12,13.5 12,10Z',
    blinkLeft:  'M4,10 Q8,8.5 12,10',
    blinkRight: 'M12,10 Q16,8.5 20,10',
  },
  // Alert/Curious — wide, attentive, pupils visible
  bright: {
    left:  'M3,10 Q3,4 8,4 Q13,4 13,10 Q13,14.5 8,14.5 Q3,14.5 3,10Z',
    right: 'M11,10 Q11,4 16,4 Q21,4 21,10 Q21,14.5 16,14.5 Q11,14.5 11,10Z',
    blinkLeft:  'M3,10 Q8,8 13,10',
    blinkRight: 'M11,10 Q16,8 21,10',
  },
  // Stressed — asymmetric tension, worried brow feel
  worried: {
    left:  'M4.5,11 Q4.5,7 8,6.5 Q11.5,6 12,10.5 Q12,14 8,14 Q4.5,14 4.5,11Z',
    right: 'M12,10.5 Q12.5,6 16,6.5 Q19.5,7 19.5,11 Q19.5,14 16,14 Q12,14 12,10.5Z',
    blinkLeft:  'M4.5,10.5 Q8,9.5 12,10.5',
    blinkRight: 'M12,10.5 Q16,9.5 19.5,10.5',
  },
  // Panic — very wide, startled, round
  wide: {
    left:  'M3,9.5 Q3,2.5 8,2.5 Q13,2.5 13,9.5 Q13,15.5 8,15.5 Q3,15.5 3,9.5Z',
    right: 'M11,9.5 Q11,2.5 16,2.5 Q21,2.5 21,9.5 Q21,15.5 16,15.5 Q11,15.5 11,9.5Z',
    blinkLeft:  'M3,9.5 Q8,7.5 13,9.5',
    blinkRight: 'M11,9.5 Q16,7.5 21,9.5',
  },
  // Look up — big + shifted upward
  lookUp: {
    left:  'M4,8.5 Q4,3 8,3 Q12,3 12,8.5 Q12,12.5 8,12.5 Q4,12.5 4,8.5Z',
    right: 'M12,8.5 Q12,3 16,3 Q20,3 20,8.5 Q20,12.5 16,12.5 Q12,12.5 12,8.5Z',
    blinkLeft:  'M4,8 Q8,6.5 12,8',
    blinkRight: 'M12,8 Q16,6.5 20,8',
  },
  // Hyped — bold star eyes
  star: {
    left:  'M8,2 L9.5,6.8 L13.5,8 L9.5,9.2 L8,14 L6.5,9.2 L2.5,8 L6.5,6.8Z',
    right: 'M16,2 L17.5,6.8 L21.5,8 L17.5,9.2 L16,14 L14.5,9.2 L10.5,8 L14.5,6.8Z',
    blinkLeft:  'M3,8 Q8,6 13,8',
    blinkRight: 'M11,8 Q16,6 21,8',
  },
  // Deadpan — very heavy lids, barely open, bored
  deadpan: {
    left:  'M4.5,11 Q4.5,9.5 8,9.5 Q11.5,9.5 11.5,11 Q11.5,12.5 8,12.5 Q4.5,12.5 4.5,11Z',
    right: 'M12.5,11 Q12.5,9.5 16,9.5 Q19.5,9.5 19.5,11 Q19.5,12.5 16,12.5 Q12.5,12.5 12.5,11Z',
    blinkLeft:  'M4.5,11 Q8,10.5 11.5,11',
    blinkRight: 'M12.5,11 Q16,10.5 19.5,11',
  },
  // Dizzy — X eyes for volatility shock
  dizzy: {
    left:  'M5,6.5 L8.5,10 M8.5,6.5 L5,10',
    right: 'M15.5,6.5 L19,10 M19,6.5 L15.5,10',
    blinkLeft:  'M5,8.5 Q6.5,8 8.5,8.5',
    blinkRight: 'M15.5,8.5 Q17.5,8 19,8.5',
  },
} as const;

// ── MOUTHS ──
// Bolder, more distinct, better readable at small sizes
const MOUTHS = {
  // Calm — small gentle smile
  smallSmile:  { d: 'M9,4.5 Q12,7.5 15,4.5' },
  // Vibing — wider warm smile
  smile:       { d: 'M7.5,3.5 Q12,9.5 16.5,3.5' },
  // Hyped — big confident grin
  grin:        { d: 'M6.5,3 Q12,11.5 17.5,3' },
  // Alert — flat, focused
  flat:        { d: 'M8,5.5 L16,5.5' },
  // Worried — visible frown curve
  worried:     { d: 'M9,7.5 Q12,3.5 15,7.5' },
  // Shaken — deeper frown
  frown:       { d: 'M7.5,8 Q12,2.5 16.5,8' },
  // Stressed — tense zigzag mouth
  tense:       { d: 'M7,5.5 Q9.5,3 12,5.5 Q14.5,3 17,5.5' },
  // Block found — open happy
  openSmile:   { d: 'M7.5,3 Q12,10.5 16.5,3 Q12,5.5 7.5,3Z', fill: true },
  // Surprised — O shape
  oh:          { d: 'M9.5,3 Q9.5,7.5 12,7.5 Q14.5,7.5 14.5,3 Q14.5,7.5 12,9 Q9.5,7.5 9.5,3Z', fill: true },
  // Gulp — open round, receiving bubbles
  gulp:        { d: 'M9,2.5 Q9,8 12,8.5 Q15,8 15,2.5 Q15,8 12,10 Q9,8 9,2.5Z', fill: true },
  // Satisfied close — tiny content smile after swallowing
  satisfiedClose: { d: 'M8.5,4 Q12,8 15.5,4' },
  // Relieved — wide soft exhale smile
  relieved:    { d: 'M7,3.5 Q12,10 17,3.5' },
  // Smug — asymmetric smirk, one side slightly higher
  smug:        { d: 'M9,5 Q11,3.5 12,4.5 Q14,7 16,4' },
  // Deadpan — perfectly flat thin line, dry
  deadpanLine: { d: 'M8.5,6 L15.5,6' },
} as const;

// ═══════════════════════════════════════════════════════
// LAYER 2: Aura config per mood
// ═══════════════════════════════════════════════════════

export interface AuraConfig {
  color: string;
  intensity: number;
  pulseSpeed: number;
  size: number;
  rings: number;
}

// ═══════════════════════════════════════════════════════
// LAYER 3: Body motion presets (framer-motion configs)
// REDESIGNED with weight, inertia, squash/stretch, stillness
// ═══════════════════════════════════════════════════════

export type MotionPreset =
  | 'idleFloat' | 'slowPulse' | 'microSway'
  | 'lookAround' | 'nervousWobble' | 'shake'
  | 'happyBounce' | 'relievedExhale' | 'powerGlow'
  | 'droop' | 'sideSway' | 'recoil'
  | 'dizzyWobble' | 'deadpanDrift'
  | 'alertStill' | 'stressedJitter' | 'hypedLift'
  | 'fearShrink' | 'greedSwell' | 'reliefExhaleDeep'
  | 'anxiousGlance' | 'celebrationSpin' | 'springJump';

export interface MotionConfig {
  animate: Record<string, number | number[]>;
  transition: Record<string, unknown>;
}

export const MOTION_PRESETS: Record<MotionPreset, MotionConfig> = {
  // Calm — very gentle settled breathing, barely lifts off ground
  idleFloat: {
    animate: { y: [0, -3, 0], scaleX: [1, 1.005, 1], scaleY: [1, 0.995, 1] },
    transition: { duration: 5, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  slowPulse: {
    animate: { scale: [1, 1.03, 1] },
    transition: { duration: 4.5, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  // Vibing — relaxed sway with body weight
  microSway: {
    animate: { x: [0, 2, -2, 0], y: [0, -3, 0], rotate: [0, 0.8, -0.8, 0] },
    transition: { duration: 6, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  // Alert — mostly still, tense, minimal movement = tension
  alertStill: {
    animate: { y: [0, -2, 0], scaleY: [1, 1.005, 1] },
    transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
  },
  lookAround: {
    animate: { x: [0, 5, -5, 3, 0], y: [0, -3, -1, -4, 0] },
    transition: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
  },
  // Stressed — jittery, compressed, pressurized
  stressedJitter: {
    animate: { x: [0, -1.5, 2, -2, 1, 0], y: [0, 1, 0, 1, 0], scaleY: [1, 0.98, 1, 0.98, 1] },
    transition: { duration: 0.8, repeat: Infinity, ease: 'easeInOut' },
  },
  nervousWobble: {
    animate: { x: [0, -2, 2.5, -1.5, 1, 0], y: [0, -2, 0, -1, 0] },
    transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
  },
  shake: {
    animate: { x: [0, -3, 3.5, -2.5, 2, 0], y: [0, -1.5, 0], scaleX: [1, 1.02, 0.98, 1.01, 1] },
    transition: { duration: 0.55, repeat: Infinity, ease: 'easeInOut' },
  },
  // Happy — strong upward pop with squash-stretch and overshoot
  happyBounce: {
    animate: {
      y: [0, -22, 4, -8, 0],
      scaleX: [1, 0.9, 1.15, 0.95, 1],
      scaleY: [1, 1.15, 0.88, 1.05, 1],
      rotate: [0, -5, 5, -2, 0],
    },
    transition: { duration: 1, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
  // Relieved — gentle settle, barely moves, weight sinks down peacefully
  relievedExhale: {
    animate: {
      y: [0, 2, -1, 0],
      scaleX: [1, 1.01, 1.0, 1],
      scaleY: [1, 0.99, 1.005, 1],
    },
    transition: { duration: 4.5, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  // Hyped — lifted posture, confident, chest-out
  hypedLift: {
    animate: { y: [0, -6, -4], scaleY: [1, 1.04, 1.02], scaleX: [1, 0.98, 0.99] },
    transition: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
  },
  powerGlow: {
    animate: { y: [0, -8, 0], scaleY: [1, 1.06, 1], scaleX: [1, 0.96, 1] },
    transition: { duration: 1.6, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  // Shaken — heavy droop, sinking
  droop: {
    animate: { y: [0, 6, 4], scaleY: [1, 0.95, 0.96], scaleX: [1, 1.03, 1.02] },
    transition: { duration: 3.5, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  sideSway: {
    animate: { x: [0, 7, -7, 0], y: [0, -1.5, 0] },
    transition: { duration: 5, repeat: Infinity, ease: 'easeInOut' },
  },
  // Fee spike — sharp recoil with squash
  recoil: {
    animate: {
      scaleX: [1, 1.12, 0.92, 1.04, 1],
      scaleY: [1, 0.82, 1.12, 0.97, 1],
      y: [0, 6, -10, 0],
    },
    transition: { duration: 0.6, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
  dizzyWobble: {
    animate: { rotate: [0, -7, 7, -5, 5, 0], x: [0, -4, 4, 0] },
    transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
  },
  // Deadpan — almost nothing. Stillness IS the character acting.
  deadpanDrift: {
    animate: { x: [0, 1, -1, 0], y: [0, -0.5, 0] },
    transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
  },
  // Fear — shrink inward, startled
  fearShrink: {
    animate: { scale: [1, 0.93, 0.95, 1], y: [0, 3, 1, 0] },
    transition: { duration: 2.5, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
  // Greed — swell with confidence
  greedSwell: {
    animate: { scale: [1, 1.06, 1.03, 1], y: [0, -8, -3, 0] },
    transition: { duration: 2.0, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
  // Deep relief exhale — compress then expand
  reliefExhaleDeep: {
    animate: {
      scaleX: [1, 1.04, 0.97, 1],
      scaleY: [1, 0.94, 1.04, 1],
      y: [0, 3, -2, 0],
    },
    transition: { duration: 2.0, repeat: 0, ease: [0.45, 0.05, 0.55, 0.95] },
  },
  // Anxious — looking left and right
  anxiousGlance: {
    animate: { x: [0, -5, 5, -3, 3, 0], rotate: [0, -3, 3, -2, 2, 0] },
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
  },
  // Full celebration — bounce + spin
  celebrationSpin: {
    animate: {
      y: [0, -25, 5, -12, 0],
      rotate: [0, 0, 360, 360, 0],
      scaleX: [1, 0.88, 1.15, 0.95, 1],
      scaleY: [1, 1.18, 0.85, 1.05, 1],
    },
    transition: { duration: 1.6, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
  // Enhanced block jump — spring physics
  springJump: {
    animate: {
      y: [0, -18, 6, -6, 2, 0],
      scaleX: [1, 0.88, 1.14, 0.96, 1.02, 1],
      scaleY: [1, 1.16, 0.84, 1.06, 0.98, 1],
      rotate: [0, -4, 4, -2, 0],
    },
    transition: { duration: 1.2, repeat: 0, ease: [0.34, 1.56, 0.64, 1] },
  },
};

// ═══════════════════════════════════════════════════════
// LAYER 4: Blink pattern config
// ═══════════════════════════════════════════════════════

export interface BlinkConfig {
  interval: number;
  duration: number;
  doubleBlink: boolean;
}

// ═══════════════════════════════════════════════════════
// MASTER EXPRESSION MAP
// ═══════════════════════════════════════════════════════

export interface BuddyExpression {
  eyes: EyeShape;
  mouth: MouthShape;
  aura: AuraConfig;
  motionPreset: MotionPreset;
  blink: BlinkConfig;
  bodyGradient: [string, string];
  statusTexts: string[];
  accessories?: ('sweat' | 'sparkle' | 'zzz')[];
  /** Body shape modifiers for posture/mass */
  bodyShape?: {
    scaleX?: number;
    scaleY?: number;
    borderRadius?: string;
  };
}

export const EXPRESSION_MAP: Record<BuddyMood, BuddyExpression> = {
  calm: {
    eyes: EYES.halfClosed,
    mouth: MOUTHS.smallSmile,
    aura: { color: '--bitcoin', intensity: 0.04, pulseSpeed: 0, size: 0.95, rings: 1 },
    motionPreset: 'idleFloat',
    blink: { interval: 7000, duration: 380, doubleBlink: false },
    bodyGradient: ['hsl(var(--bitcoin) / 0.65)', 'hsl(var(--bitcoin-glow) / 0.45)'],
    statusTexts: ['Just vibing...', 'Calm chain.', 'Quiet block space.', 'Working as designed.', 'Still Bitcoin.'],
    bodyShape: { scaleX: 1.0, scaleY: 1.0, borderRadius: '50% 50% 52% 48% / 48% 48% 52% 52%' },
  },
  vibing: {
    eyes: EYES.soft,
    mouth: MOUTHS.smallSmile,
    aura: { color: '--bitcoin', intensity: 0.06, pulseSpeed: 0, size: 1.0, rings: 1 },
    motionPreset: 'microSway',
    blink: { interval: 5500, duration: 280, doubleBlink: false },
    bodyGradient: ['hsl(var(--bitcoin) / 0.7)', 'hsl(var(--bitcoin-glow) / 0.55)'],
    statusTexts: ['Watching the blocks roll in', 'Good vibes.', 'Smooth sailing.', 'Tick tock.'],
    bodyShape: { scaleX: 1.0, scaleY: 1.0, borderRadius: '50% 50% 50% 50% / 48% 48% 52% 52%' },
  },
  curious: {
    eyes: EYES.bright,
    mouth: MOUTHS.flat,
    aura: { color: '--accent', intensity: 0.18, pulseSpeed: 0, size: 1, rings: 1 },
    motionPreset: 'alertStill',
    blink: { interval: 3000, duration: 150, doubleBlink: true },
    bodyGradient: ['hsl(var(--bitcoin) / 0.7)', 'hsl(var(--accent) / 0.35)'],
    statusTexts: ['Queue building.', 'Traffic up.', "Something's cooking."],
    bodyShape: { scaleX: 0.98, scaleY: 1.02, borderRadius: '48% 52% 50% 50% / 46% 46% 54% 54%' },
  },
  alert: {
    eyes: EYES.bright,
    mouth: MOUTHS.worried,
    aura: { color: '--warning', intensity: 0.28, pulseSpeed: 2.5, size: 1.1, rings: 2 },
    motionPreset: 'alertStill',
    blink: { interval: 2500, duration: 120, doubleBlink: true },
    bodyGradient: ['hsl(var(--warning) / 0.75)', 'hsl(var(--bitcoin) / 0.55)'],
    statusTexts: ['Whoa.', 'Big move.', 'Mood swing detected.'],
    bodyShape: { scaleX: 0.97, scaleY: 1.03, borderRadius: '48% 52% 50% 50% / 45% 45% 55% 55%' },
  },
  stressed: {
    eyes: EYES.worried,
    mouth: MOUTHS.tense,
    aura: { color: '--stress', intensity: 0.32, pulseSpeed: 1.8, size: 1.15, rings: 2 },
    motionPreset: 'stressedJitter',
    blink: { interval: 2000, duration: 100, doubleBlink: false },
    bodyGradient: ['hsl(var(--stress) / 0.65)', 'hsl(var(--bitcoin) / 0.45)'],
    statusTexts: ['Running hot.', 'Under pressure.', 'Feeling the heat.', 'Scarcity is visible.'],
    bodyShape: { scaleX: 1.04, scaleY: 0.95, borderRadius: '52% 48% 48% 52% / 52% 52% 48% 48%' },
  },
  relieved: {
    eyes: EYES.halfClosed,
    mouth: MOUTHS.relieved,
    aura: { color: '--success', intensity: 0.08, pulseSpeed: 0, size: 1.0, rings: 1 },
    motionPreset: 'relievedExhale',
    blink: { interval: 5500, duration: 450, doubleBlink: false },
    bodyGradient: ['hsl(var(--success) / 0.4)', 'hsl(var(--bitcoin) / 0.55)'],
    statusTexts: ['Fresh block space.', 'Pressure easing.', 'That helped.'],
    bodyShape: { scaleX: 1.0, scaleY: 1.0, borderRadius: '50% 50% 54% 46% / 48% 48% 52% 52%' },
  },
  hyped: {
    eyes: EYES.star,
    mouth: MOUTHS.grin,
    aura: { color: '--bitcoin', intensity: 0.4, pulseSpeed: 1.2, size: 1.3, rings: 3 },
    motionPreset: 'hypedLift',
    blink: { interval: 6000, duration: 150, doubleBlink: false },
    bodyGradient: ['hsl(var(--bitcoin))', 'hsl(var(--bitcoin-glow))'],
    statusTexts: ['Laser mode.', 'Feeling bullish.', 'Up only? maybe.', 'Tick tock.', 'Called it.'],
    accessories: ['sparkle'],
    bodyShape: { scaleX: 0.97, scaleY: 1.04, borderRadius: '48% 52% 48% 52% / 44% 44% 56% 56%' },
  },
  shaken: {
    eyes: EYES.deadpan,
    mouth: MOUTHS.frown,
    aura: { color: '--muted-foreground', intensity: 0.12, pulseSpeed: 0, size: 0.88, rings: 1 },
    motionPreset: 'droop',
    blink: { interval: 4000, duration: 450, doubleBlink: false },
    bodyGradient: ['hsl(var(--muted-foreground) / 0.45)', 'hsl(var(--bitcoin) / 0.35)'],
    statusTexts: ['Oof.', 'Bit of a dip.', 'Chain still moving.', 'Weak hands, same chain.', 'Noise.'],
    bodyShape: { scaleX: 1.05, scaleY: 0.94, borderRadius: '54% 46% 46% 54% / 54% 54% 46% 46%' },
  },
  celebrating: {
    eyes: EYES.bright,
    mouth: MOUTHS.grin,
    aura: { color: '--bitcoin', intensity: 0.45, pulseSpeed: 1.0, size: 1.35, rings: 3 },
    motionPreset: 'happyBounce',
    blink: { interval: 5000, duration: 200, doubleBlink: false },
    bodyGradient: ['hsl(var(--bitcoin))', 'hsl(var(--bitcoin-glow))'],
    statusTexts: ['Number go up!', 'Called it.', 'Conviction pays.', 'Not your keys, not your coins.', 'Tick tock. 🚀'],
    accessories: ['sparkle'],
    bodyShape: { scaleX: 0.96, scaleY: 1.06, borderRadius: '46% 54% 48% 52% / 42% 42% 58% 58%' },
  },
};

// ═══════════════════════════════════════════════════════
// TRANSIENT EVENT REACTIONS
// ═══════════════════════════════════════════════════════

export type TransientReaction =
  | 'blockFound' | 'feeSpike' | 'pricePump'
  | 'priceDip' | 'longWait' | 'backToBack' | 'volatilityShock'
  | 'fearSurge' | 'greedPeak' | 'feeRelief' | 'droughtAnxiety' | 'halvingHype';

export interface MouthSequenceStep {
  mouth: MouthShape;
  durationMs: number;
}

export interface TransientConfig {
  motionPreset: MotionPreset;
  durationMs: number;
  statusText: string;
  eyeOverride?: EyeShape;
  mouthOverride?: MouthShape;
  /** Phased mouth animation — overrides mouthOverride when present */
  mouthSequence?: MouthSequenceStep[];
  auraFlash?: { color: string; intensity: number };
  particles?: string[];
}

export const TRANSIENT_REACTIONS: Record<TransientReaction, TransientConfig> = {
  blockFound: {
    motionPreset: 'springJump',
    durationMs: 2500,
    statusText: 'Block found! ⛏️',
    eyeOverride: EYES.bright,
    mouthOverride: MOUTHS.gulp,
    /** Phased mouth sequence: eyes close (relief blink) → gulp → satisfied → smile */
    mouthSequence: [
      { mouth: MOUTHS.gulp, durationMs: 600 },
      { mouth: MOUTHS.satisfiedClose, durationMs: 500 },
      { mouth: MOUTHS.smile, durationMs: 600 },
      { mouth: MOUTHS.relieved, durationMs: 800 },
    ],
    auraFlash: { color: '--success', intensity: 0.6 },
    particles: ['⛏️', '✨', '₿', '✨'],
  },
  feeSpike: {
    motionPreset: 'recoil',
    durationMs: 1200,
    statusText: 'Fee spike! 💸',
    eyeOverride: EYES.wide,
    mouthOverride: MOUTHS.oh,
    auraFlash: { color: '--stress', intensity: 0.45 },
  },
  pricePump: {
    motionPreset: 'hypedLift',
    durationMs: 1800,
    statusText: 'Pump it! 🚀',
    eyeOverride: EYES.star,
    mouthOverride: MOUTHS.grin,
    auraFlash: { color: '--bitcoin', intensity: 0.5 },
    particles: ['🚀', '✨'],
  },
  priceDip: {
    motionPreset: 'droop',
    durationMs: 1800,
    statusText: 'Noise.',
    eyeOverride: EYES.deadpan,
    mouthOverride: MOUTHS.smug,
    auraFlash: { color: '--muted-foreground', intensity: 0.15 },
  },
  longWait: {
    motionPreset: 'alertStill',
    durationMs: 2000,
    statusText: 'Come on, miners...',
    eyeOverride: EYES.lookUp,
    mouthOverride: MOUTHS.worried,
  },
  backToBack: {
    motionPreset: 'happyBounce',
    durationMs: 2200,
    statusText: 'Back to back! ⚡',
    eyeOverride: EYES.bright,
    mouthOverride: MOUTHS.grin,
    auraFlash: { color: '--success', intensity: 0.5 },
    particles: ['⚡', '✨', '⛏️', '✨', '⚡'],
  },
  volatilityShock: {
    motionPreset: 'dizzyWobble',
    durationMs: 1500,
    statusText: 'Whoa! 🌀',
    eyeOverride: EYES.dizzy,
    mouthOverride: MOUTHS.oh,
    auraFlash: { color: '--warning', intensity: 0.35 },
  },
  fearSurge: {
    motionPreset: 'fearShrink',
    durationMs: 2500,
    statusText: 'Extreme fear... 😨',
    eyeOverride: EYES.wide,
    mouthOverride: MOUTHS.worried,
    auraFlash: { color: '--muted-foreground', intensity: 0.3 },
  },
  greedPeak: {
    motionPreset: 'greedSwell',
    durationMs: 2000,
    statusText: 'Peak greed! 🤑',
    eyeOverride: EYES.star,
    mouthOverride: MOUTHS.grin,
    auraFlash: { color: '--bitcoin', intensity: 0.55 },
    particles: ['💰', '✨', '🚀'],
  },
  feeRelief: {
    motionPreset: 'reliefExhaleDeep',
    durationMs: 2000,
    statusText: 'Fees dropping! 😌',
    eyeOverride: EYES.halfClosed,
    mouthOverride: MOUTHS.relieved,
    auraFlash: { color: '--success', intensity: 0.4 },
  },
  droughtAnxiety: {
    motionPreset: 'anxiousGlance',
    durationMs: 4000,
    statusText: 'Where are the blocks? 👀',
    eyeOverride: EYES.worried,
    mouthOverride: MOUTHS.tense,
  },
  halvingHype: {
    motionPreset: 'celebrationSpin',
    durationMs: 4000,
    statusText: 'HALVING SOON! 🎰',
    eyeOverride: EYES.star,
    mouthOverride: MOUTHS.grin,
    auraFlash: { color: '--bitcoin', intensity: 0.6 },
    particles: ['🎰', '⚡', '✨', '₿', '🚀', '✨'],
  },
};
